<template>
  <div class="color-theme-picker">
    <button
      ref="buttonRef"
      class="picker-button"
      title="颜色主题"
      @click="showModal = true"
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path
          fill-rule="evenodd"
          d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z"
          clip-rule="evenodd"
        />
      </svg>
    </button>

    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h2 class="modal-title">颜色主题</h2>
              <button class="modal-close" @click="showModal = false">×</button>
            </div>
            <div class="modal-body">
              <!-- 预设主题 -->
              <div class="section">
                <h3 class="section-title">预设主题</h3>
                <div class="theme-grid">
                  <button
                    v-for="theme in colorThemesList"
                    :key="theme.id"
                    :class="['theme-card', { active: currentColorTheme === theme.id }]"
                    :title="theme.name"
                    @click="selectTheme(theme.id)"
                  >
                    <div class="theme-preview">
                      <div
                        class="theme-color-bar"
                        :style="{
                          background: `linear-gradient(135deg, ${theme.light.primary} 0%, ${theme.light.secondary} 100%)`,
                        }"
                      ></div>
                      <div class="theme-info">
                        <div class="theme-name">{{ theme.name }}</div>
                        <div class="theme-desc">{{ theme.description }}</div>
                      </div>
                      <div v-if="currentColorTheme === theme.id" class="theme-check">✓</div>
                    </div>
                  </button>
                </div>
              </div>

              <!-- 自定义主题 -->
              <div class="section">
                <h3 class="section-title">自定义颜色</h3>
                <div class="custom-colors">
                  <div class="color-input-group">
                    <label>主色调</label>
                    <div class="color-input-wrapper">
                      <input
                        v-model="customPrimary"
                        type="color"
                        class="color-input"
                        @input="updateCustomTheme"
                      />
                      <input
                        v-model="customPrimaryHex"
                        type="text"
                        class="hex-input"
                        maxlength="7"
                        @input="handleHexInput('primary')"
                      />
                    </div>
                  </div>
                  <div class="color-input-group">
                    <label>辅助色</label>
                    <div class="color-input-wrapper">
                      <input
                        v-model="customSecondary"
                        type="color"
                        class="color-input"
                        @input="updateCustomTheme"
                      />
                      <input
                        v-model="customSecondaryHex"
                        type="text"
                        class="hex-input"
                        maxlength="7"
                        @input="handleHexInput('secondary')"
                      />
                    </div>
                  </div>
                </div>
                <button
                  class="apply-custom-button"
                  :disabled="!isCustomThemeActive"
                  @click="applyCustomTheme"
                >
                  应用自定义主题
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useThemeStore, colorThemes } from '../stores/theme';

const themeStore = useThemeStore();
const { colorTheme: currentColorTheme } = storeToRefs(themeStore);
const { setColorTheme } = themeStore;

const showModal = ref(false);
const buttonRef = ref<HTMLButtonElement>();

const customPrimary = ref('#667eea');
const customSecondary = ref('#764ba2');
const customPrimaryHex = ref('#667eea');
const customSecondaryHex = ref('#764ba2');

const colorThemesList = computed(() => Object.values(colorThemes));
const isCustomThemeActive = computed(() => currentColorTheme.value === 'custom');

// 同步颜色选择器和十六进制输入
watch(customPrimary, (newVal) => {
  customPrimaryHex.value = newVal;
});

watch(customSecondary, (newVal) => {
  customSecondaryHex.value = newVal;
});

function selectTheme(themeId: string) {
  setColorTheme(themeId);
}

function handleHexInput(color: 'primary' | 'secondary') {
  const hex = color === 'primary' ? customPrimaryHex.value : customSecondaryHex.value;
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    if (color === 'primary') {
      customPrimary.value = hex;
    } else {
      customSecondary.value = hex;
    }
  }
}

function updateCustomTheme() {
  // 实时预览自定义主题
  if (isCustomThemeActive.value) {
    applyCustomTheme();
  }
}

function applyCustomTheme() {
  // 应用自定义主题到 CSS 变量
  const root = document.documentElement;
  const isDark = document.documentElement.classList.contains('dark');

  // 设置主色调和辅助色
  root.style.setProperty('--color-primary', customPrimary.value);
  root.style.setProperty('--color-secondary', customSecondary.value);

  // 计算浅色变体
  root.style.setProperty('--color-primary-light', adjustBrightness(customPrimary.value, 20));

  // 确保文本和背景颜色变量被正确设置（使用当前明暗模式的默认值）
  if (isDark) {
    root.style.setProperty('--bg-primary', '#111827');
    root.style.setProperty('--bg-secondary', '#1f2937');
    root.style.setProperty('--bg-tertiary', '#374151');
    root.style.setProperty('--text-primary', '#f9fafb');
    root.style.setProperty('--text-secondary', '#d1d5db');
    root.style.setProperty('--text-tertiary', '#d1d5db');
    root.style.setProperty('--border-color', '#4b5563');
  } else {
    root.style.setProperty('--bg-primary', '#f5f5f5');
    root.style.setProperty('--bg-secondary', '#ffffff');
    root.style.setProperty('--bg- tertiary', '#f3f4f6');
    root.style.setProperty('--text-primary', '#1f2937');
    root.style.setProperty('--text-secondary', '#6b7280');
    root.style.setProperty('--text-tertiary', '#9ca3af');
    root.style.setProperty('--border-color', '#e5e7eb');
  }

  // 标记为自定义主题
  setColorTheme('custom');
}

function adjustBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
}

// 监听自定义事件
onMounted(() => {
  window.addEventListener('open-color-picker', () => {
    showModal.value = true;
  });
});
</script>

<style scoped>
.color-theme-picker {
  position: relative;
}

.picker-button {
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px var(--shadow-color);
}

.picker-button:hover {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
  color: var(--color-primary);
  transform: scale(1.1);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.picker-button svg {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.picker-button:hover svg {
  transform: rotate(15deg);
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 560px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 8px;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--border-color);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.section {
  margin-bottom: 2rem;
}

.section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
}

/* 预设主题网格 */
.theme-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.theme-card {
  border: 2px solid var(--border-color);
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.theme-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.theme-card.active {
  border-color: var(--color-primary);
  background: rgba(102, 126, 234, 0.05);
}

.theme-preview {
  position: relative;
}

.theme-color-bar {
  height: 60px;
  border-radius: 10px 10px 0 0;
}

.theme-info {
  padding: 1rem;
}

.theme-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.theme-desc {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.theme-check {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: bold;
}

/* 自定义颜色 */
.custom-colors {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.color-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.color-input-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.color-input-wrapper {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.color-input {
  width: 48px;
  height: 40px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  background: transparent;
}

.color-input::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-input::-webkit-color-swatch {
  border: none;
  border-radius: 6px;
}

.hex-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-primary);
  border-radius: 8px;
  font-family: monospace;
  font-size: 0.875rem;
  text-transform: uppercase;
}

.hex-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.apply-custom-button {
  width: 100%;
  padding: 0.875rem;
  border: none;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.apply-custom-button:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.apply-custom-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 弹窗动画 */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}

/* 暗夜模式 */
:global(.dark) .picker-button {
  background: var(--bg-secondary);
  border-color: var(--border-color);
}

:global(.dark) .picker-button:hover {
  border-color: var(--color-primary-light);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

/* 移动端优化 */
@media (max-width: 640px) {
  .modal-content {
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    margin-top: auto;
  }

  .modal-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .theme-grid {
    grid-template-columns: 1fr;
  }

  .custom-colors {
    grid-template-columns: 1fr;
  }
}
</style>
